import { ApiRequestError, ReportReason, type CommunityEntry } from "@cfg/api-contract";
import { FitPicker, type FitPickerHandle, type FitTagValue } from "@cfg/configurator-ui";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState } from "react";
import { useApp } from "../app-context.tsx";
import { useAuthUser } from "../auth.ts";
import { useFitSources } from "../fits.ts";
import { useMe } from "../me.ts";
import { likesQueryKey, useCommunityCache, useEntryCode, useEntryName, useFitPrintsText, useMyCommunity, useMyLikes } from "./CommunityPanel.tsx";
import { DisplayNameDialog } from "./DisplayNameDialog.tsx";

const Star = ({ filled }: { filled: boolean }) => (
  <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden focusable="false">
    <path
      d="M10 2.2l2.4 4.9 5.4.8-3.9 3.8.9 5.4L10 14.6l-4.8 2.5.9-5.4L2.2 7.9l5.4-.8z"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </svg>
);
const LinkIcon = () => (
  <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden focusable="false">
    <path d="M8.5 11.5a3 3 0 0 0 4.2 0l2.8-2.8a3 3 0 0 0-4.2-4.2l-1 1M11.5 8.5a3 3 0 0 0-4.2 0l-2.8 2.8a3 3 0 0 0 4.2 4.2l1-1" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);
const Check = () => (
  <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden focusable="false">
    <path d="M4.5 10.5l3.5 3.5 7.5-8" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const Flag = () => (
  <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden focusable="false">
    <path d="M5 17V3.5M5 4h9l-2 3.5 2 3.5H5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export interface CommunityEntryBarProps {
  /** The community entry on the model. */
  entry: CommunityEntry;
  /** Design open in the configurator (its community list is the one to keep in step). */
  designId: string;
  /** Put the user's own params back. */
  onRestore: () => void;
  /** The set list drops over what's below (the 3D view) instead of pushing it down. */
  floating?: boolean;
}

/**
 * The community bracket on the model, as one line: its parameters, "N sets ▾",
 * favorite and share as icons, and the way back to your own dimensions. The
 * set toggle opens its sets, one per row, with "I printed it", your own tags'
 * ×, a report flag and "+ Add a set". Above the 3D view on wide screens, where
 * the list drops over the model (`floating`) so a long one never moves it;
 * atop the Community tab on phones, where it opens in place.
 */
export function CommunityEntryBar({ entry: e, designId, onRestore, floating = false }: CommunityEntryBarProps) {
  const { api, auth, brand, copy } = useApp();
  const user = useAuthUser(auth);
  const me = useMe();
  const qc = useQueryClient();
  const cache = useCommunityCache(designId);
  const nameOf = useEntryName();
  const codeOf = useEntryCode();
  const printsText = useFitPrintsText();
  const likes = useMyLikes();
  const mine = useMyCommunity();
  const fitSources = useFitSources();
  const fitConfig = brand.config.fitTags;
  const myTags = new Set(mine.data?.tags ?? []);
  const myPrints = new Set(mine.data?.prints ?? []);
  const liked = (likes.data ?? []).some((i) => i.id === e.id);

  const [msg, setMsg] = useState<{ text: string; error?: boolean; undo?: () => void } | null>(null);
  /**
   * Your tag whose removal waits for "Remove": the entry's last one (it takes the entry with it) or one
   * other people registered prints on (those go with it, and Undo can't bring them back).
   */
  const [confirmLast, setConfirmLast] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [printed, setPrinted] = useState(false);
  const [awaitingName, setAwaitingName] = useState<FitTagValue | null>(null);
  const [reporting, setReporting] = useState<string | null>(null);
  const [reason, setReason] = useState<ReportReason>("wrong-fit");
  const [note, setNote] = useState("");
  const picker = useRef<FitPickerHandle>(null);
  const [openSets, setOpenSets] = useState(false);
  /** The link was just copied: the share icon shows ✓ and a "Link copied" bubble for a moment. */
  const [copied, setCopied] = useState(false);
  /** Clicks per icon button: each one re-mounts the icon, replaying its press "pop" (CSS `.entry-icon-glyph.is-pop`). */
  const [pops, setPops] = useState({ fav: 0, share: 0 });
  const pop = (which: keyof typeof pops) => setPops((p) => ({ ...p, [which]: p[which] + 1 }));
  const glyph = (which: keyof typeof pops, icon: React.ReactNode) => (
    <span key={pops[which]} className={`entry-icon-glyph${pops[which] ? " is-pop" : ""}`}>
      {icon}
    </span>
  );
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1800);
    return () => clearTimeout(timer);
  }, [copied]);
  const root = useRef<HTMLElement>(null);
  const listId = useId();

  // Another entry on the model: start clean.
  useEffect(() => {
    setMsg(null);
    setAdding(false);
    setReporting(null);
    setOpenSets(false);
  }, [e.id]);
  // A floating list closes on Escape and on a click anywhere else (not while the name dialog is up).
  useEffect(() => {
    if (!floating || !openSets) return;
    const onDown = (ev: PointerEvent) => {
      if (awaitingName || root.current?.contains(ev.target as Node)) return;
      setOpenSets(false);
    };
    const onKey = (ev: KeyboardEvent) => ev.key === "Escape" && !awaitingName && setOpenSets(false);
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [floating, openSets, awaitingName]);
  // Status lines fade after a while, so they always describe the last action; not while pointed at or
  // focused (time to read, or to reach Undo), and errors stay until the next action.
  const [msgHeld, setMsgHeld] = useState(false);
  useEffect(() => {
    if (!msg || msg.error || msgHeld) return;
    const timer = setTimeout(() => setMsg(null), 6000);
    return () => clearTimeout(timer);
  }, [msg, msgHeld]);
  const holdMsg = { onMouseEnter: () => setMsgHeld(true), onMouseLeave: () => setMsgHeld(false), onFocus: () => setMsgHeld(true), onBlur: () => setMsgHeld(false) };
  const fail = (err: unknown) => setMsg({ text: (err as Error).message, error: true });
  const signedIn = () => {
    if (user) return true;
    void auth.signIn();
    return false;
  };

  const registerPrint = async (ref: string) => {
    if (!api || !signedIn()) return;
    try {
      const r = await api.confirmPublic(e.id, ref);
      cache.upsert(r.entry, { front: false });
      cache.markMine(e.id, [], [ref]);
      setMsg({ text: copy(r.added ? "community.confirmed" : "community.alreadyRegistered") });
    } catch (err) {
      fail(err);
    }
  };
  const removeTag = async (ref: string) => {
    if (!api) return;
    const fit = e.fits.find((f) => f.ref === ref);
    const printed = myPrints.has(`${e.id}#${ref}`);
    if (needsConfirm(ref) && confirmLast !== ref) return setConfirmLast(ref);
    setConfirmLast(null);
    try {
      const r = await api.removeTag(e.id, ref);
      cache.unmarkTag(e.id, ref);
      if (r.entry) {
        cache.upsert(r.entry, { front: false });
        setMsg({ text: copy("community.tagRemoved", { ref }), undo: () => void retag({ ref, name: fit?.name }, printed) });
      } else {
        cache.drop(e.id);
        onRestore();
      }
    } catch (err) {
      fail(err);
    }
  };
  /** Prints other people registered on one of your tags. */
  const othersPrints = (ref: string) => (e.fits.find((f) => f.ref === ref)?.confirmCount ?? 0) - (myPrints.has(`${e.id}#${ref}`) ? 1 : 0);
  const needsConfirm = (ref: string) => e.fits.length === 1 || othersPrints(ref) > 0;
  /** Undo a removal: the same set goes back on as your tag (with your print, if you had registered one). */
  const retag = async (fit: FitTagValue, printed: boolean) => {
    if (!api) return;
    try {
      const r = await api.tagDesign(e.designId, { version: e.version, params: e.params, fit, printed });
      cache.upsert(r.entry, { front: false });
      cache.markMine(r.entry.id, r.added ? [fit.ref] : [], printed ? [fit.ref] : []);
      setMsg({ text: copy("community.tagRestored", { ref: fit.ref }) });
    } catch (err) {
      fail(err);
    }
  };
  /** "Add set": the set in the field becomes the user's tag on this entry. No display name yet: ask for one first. */
  const addSet = () => {
    if (!api || busy || !signedIn()) return;
    const fit = picker.current?.take();
    if (!fit) return;
    if (me.data && !me.data.displayName) return setAwaitingName(fit);
    void submitSet(fit);
  };
  const submitSet = async (fit: FitTagValue) => {
    if (!api) return;
    setBusy(true);
    try {
      const r = await api.tagDesign(e.designId, { version: e.version, params: e.params, fit, printed });
      cache.upsert(r.entry, { front: false });
      cache.markMine(e.id, r.added ? [fit.ref] : [], printed ? [fit.ref] : []);
      setMsg({ text: copy(r.added ? "community.addedFit" : "community.alreadyHasFit", { refs: fit.ref }), error: !r.added && !printed });
      picker.current?.clear();
      setPrinted(false);
      setAdding(false);
    } catch (err) {
      if (err instanceof ApiRequestError && err.body?.code === "display_name_required") setAwaitingName(fit);
      else fail(err);
    } finally {
      setBusy(false);
    }
  };
  /** Favorite on or off; taking it off says so, with Undo. */
  const setFavorite = async (on: boolean) => {
    if (!api || !signedIn()) return;
    try {
      const r = on ? await api.likePublic(e.id) : await api.unlikePublic(e.id);
      cache.upsert({ ...e, likeCount: r.likeCount }, { front: false });
      qc.setQueryData(likesQueryKey(brand.config.id, user!.sub), (old: CommunityEntry[] | undefined) =>
        r.liked ? [{ ...e, likeCount: r.likeCount }, ...(old ?? []).filter((i) => i.id !== e.id)] : (old ?? []).filter((i) => i.id !== e.id),
      );
      setMsg(r.liked ? { text: copy("community.favorited") } : { text: copy("community.unfavorited"), undo: () => void setFavorite(true) });
    } catch (err) {
      fail(err);
    }
  };
  const toggleFavorite = () => setFavorite(!liked);
  const copyLink = async () => {
    const url = `${window.location.origin}/design/${encodeURIComponent(e.designId)}?community=${encodeURIComponent(e.id)}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // No clipboard access: show the link to copy by hand.
      setMsg({ text: url });
    }
  };
  const report = async (ref: string) => {
    if (!api) return;
    try {
      await api.reportPublic(e.id, { ref, reason, note: note.trim() || undefined });
      setMsg({ text: copy("community.reported") });
      setReporting(null);
      setNote("");
    } catch (err) {
      fail(err);
    }
  };

  return (
    <section ref={root} className={`entry-bar${floating ? " is-floating" : ""}${openSets ? " is-open" : ""}`} aria-label={copy("community.onModelLabel")}>
      <div className="entry-bar-head">
        <div className="entry-bar-title">
          {/* With the list closed, the last action's status takes the eyebrow's place, so the bar keeps its height. */}
          {msg && !openSets ? (
            <span className={`entry-bar-eyebrow entry-bar-status${msg.error ? " is-error" : ""}`} role="status" {...holdMsg}>
              {msg.text}
              {msg.undo && (
                <>
                  {" "}
                  <button type="button" className="link entry-undo" onClick={() => (setMsg(null), msg.undo!())}>
                    {copy("community.undo")}
                  </button>
                </>
              )}
            </span>
          ) : (
            <span className="entry-bar-eyebrow">{copy("community.onModelLabel")}</span>
          )}
          <strong className="entry-bar-name" title={copy("mine.codeHint", { code: codeOf(e) })}>
            {nameOf(e)}
          </strong>
        </div>
        <button type="button" className={`entry-sets-toggle${openSets ? " is-open" : ""}`} aria-expanded={openSets} aria-controls={listId} onClick={() => setOpenSets((o) => !o)}>
          {e.fits.length === 1 ? copy("community.sets.one", { label: fitConfig.label }) : copy("community.sets.other", { count: e.fits.length, label: fitConfig.label })}
          <span className="entry-sets-chevron" aria-hidden>
            ▾
          </span>
        </button>
        <div className="entry-bar-icons">
          <button
            type="button"
            className={`entry-icon${liked ? " is-on" : ""}`}
            aria-pressed={liked}
            aria-label={copy("community.favorite")}
            title={copy("community.favorite")}
            onClick={() => {
              pop("fav");
              void toggleFavorite();
            }}
          >
            {glyph("fav", <Star filled={liked} />)}
            {e.likeCount > 0 && <span className="entry-icon-count">{e.likeCount}</span>}
          </button>
          <span className="entry-icon-wrap">
            <button
              type="button"
              className={`entry-icon${copied ? " is-done" : ""}`}
              aria-label={copied ? copy("community.linkCopied") : copy("community.copyLink")}
              title={copy("community.copyLink")}
              onClick={() => {
                pop("share");
                void copyLink();
              }}
            >
              {glyph("share", copied ? <Check /> : <LinkIcon />)}
            </button>
            {copied && (
              <span className="entry-icon-tip" role="status">
                {copy("community.linkCopied")}
              </span>
            )}
          </span>
        </div>
        <button type="button" className="cfg-text-btn entry-bar-restore" onClick={onRestore}>
          ↺ {copy("community.restore")}
        </button>
      </div>

      <div className="entry-bar-drop" id={listId} hidden={!openSets}>
        <ul className="entry-sets" aria-label={copy("community.setsLabel", { label: fitConfig.label })}>
          {e.fits.map((f) => {
            const key = `${e.id}#${f.ref}`;
            const yours = myTags.has(key);
            return (
              <li key={f.ref} className={`entry-set${yours ? " is-mine" : ""}`}>
                <span className={`entry-set-prints${f.confirmCount > 0 ? " is-confirmed" : ""}`}>
                  {f.confirmCount > 0 && "✓ "}
                  {printsText(f.confirmCount)}
                </span>
                <span className="entry-set-name">
                  <strong>{f.ref}</strong>
                  {f.name && <span> {f.name}</span>}
                  {yours && <span className="entry-set-yours"> · {copy("community.yourTag")}</span>}
                </span>
                {myPrints.has(key) ? (
                  <span className="entry-set-printed">{copy("community.youPrinted")}</span>
                ) : (
                  <button type="button" className="cfg-text-btn entry-set-print" aria-label={copy("community.confirmFitLabel", { ref: f.ref })} onClick={() => void registerPrint(f.ref)}>
                    {copy("community.confirmFitShort")}
                  </button>
                )}
                {yours ? (
                  <button type="button" className="entry-set-icon" aria-label={copy("community.removeTag", { ref: f.ref })} title={copy("community.removeTag", { ref: f.ref })} onClick={() => void removeTag(f.ref)}>
                    ×
                  </button>
                ) : user ? (
                  <button
                    type="button"
                    className="entry-set-icon"
                    aria-label={copy("community.reportTag", { ref: f.ref })}
                    title={copy("community.reportTag", { ref: f.ref })}
                    aria-expanded={reporting === f.ref}
                    onClick={() => setReporting((r) => (r === f.ref ? null : f.ref))}
                  >
                    <Flag />
                  </button>
                ) : (
                  <span className="entry-set-icon" aria-hidden />
                )}
                {confirmLast === f.ref && (
                  <div className="entry-confirm" role="alert">
                    <span>
                      {e.fits.length === 1
                        ? copy("community.removeLast", { ref: f.ref })
                        : copy(othersPrints(f.ref) === 1 ? "community.removePrinted.one" : "community.removePrinted.other", { ref: f.ref, count: othersPrints(f.ref) })}
                    </span>
                    <button type="button" className="cfg-btn cfg-btn-danger" onClick={() => void removeTag(f.ref)}>
                      {copy("community.removeLastYes")}
                    </button>
                    <button type="button" className="cfg-btn" onClick={() => setConfirmLast(null)}>
                      {copy("community.removeLastNo")}
                    </button>
                  </div>
                )}
                {reporting === f.ref && (
                  <form
                    className="entry-report"
                    onSubmit={(ev) => {
                      ev.preventDefault();
                      void report(f.ref);
                    }}
                  >
                    <select className="cfg-input" value={reason} onChange={(ev) => setReason(ev.target.value as ReportReason)} aria-label={copy("community.reportReason")}>
                      {ReportReason.options.map((r) => (
                        <option key={r} value={r}>
                          {copy(`community.reason.${r}`)}
                        </option>
                      ))}
                    </select>
                    <input className="cfg-input" maxLength={300} aria-label={copy("community.reportDetails")} placeholder={copy("community.reportDetails")} value={note} onChange={(ev) => setNote(ev.target.value)} />
                    <button type="submit" className="cfg-btn">
                      {copy("community.report")}
                    </button>
                    <p className="muted small entry-report-hint">{copy("community.reportHint")}</p>
                  </form>
                )}
              </li>
            );
          })}
        </ul>

        {brand.config.features.fitTags &&
          (adding ? (
            <div className="entry-add" role="group" aria-label={copy("community.addFit", { label: fitConfig.label })}>
              <FitPicker
                ref={picker}
                label={copy("community.addFit", { label: fitConfig.label })}
                pattern={fitConfig.refPattern}
                refSuffix={fitConfig.refSuffix}
                suggestions={fitSources.suggestions}
                lookup={fitSources.lookup}
                refPlaceholder={fitConfig.refPlaceholder}
                namePlaceholder={fitConfig.namePlaceholder}
                messages={{ invalid: copy("fits.invalid"), empty: copy("community.addFitNeedsRef", { label: fitConfig.label }) }}
                onSubmit={addSet}
              />
              <label className="cfg-check">
                <input type="checkbox" checked={printed} onChange={(ev) => setPrinted(ev.target.checked)} /> {copy("community.addFitPrinted")}
              </label>
              <div className="panel-save-row">
                <button type="button" className={`cfg-btn cfg-btn-primary${busy ? " is-busy" : ""}`} disabled={busy} onClick={addSet}>
                  {copy("community.addFitSubmit")}
                </button>
                <button type="button" className="cfg-btn" onClick={() => setAdding(false)}>
                  {copy("community.cancel")}
                </button>
              </div>
            </div>
          ) : (
            <button type="button" className="cfg-text-btn entry-add-open" onClick={() => signedIn() && setAdding(true)}>
              + {copy("community.addFit", { label: fitConfig.label })}
            </button>
          ))}

        {msg && (
          <p className={msg.error ? "cfg-error entry-msg" : "muted small entry-msg"} role="status" {...holdMsg}>
            {msg.text}
            {msg.undo && (
              <>
                {" "}
                <button type="button" className="link entry-undo" onClick={() => (setMsg(null), msg.undo!())}>
                  {copy("community.undo")}
                </button>
              </>
            )}
          </p>
        )}
      </div>
      <DisplayNameDialog
        open={!!awaitingName}
        onCancel={() => setAwaitingName(null)}
        onDone={() => {
          const fit = awaitingName;
          setAwaitingName(null);
          if (fit) void submitSet(fit);
        }}
      />
    </section>
  );
}
